import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { z } from 'zod';

import {
  useAnalyticsPost,
  NarvarEventTypes,
  NarvarEventComponents,
} from '../../../hooks/Analytics';
import { usePageContext } from '../../../hooks/PageContext/usePageContext';
import { useTheme } from '../../../hooks/useTheme';
import { useSettings } from '../../../hooks/useSettings';
import styles from './CpCustomComponent.module.css';

export const PING_INTERVAL_MS = 300;

export interface MessageProps {
  guestReadyToReceiveMessages?: boolean;
  guestContentLoaded?: boolean;
  guestDocumentSize?: string;
  parentWindowId?: string;
  isHelpWidgetOpen?: boolean;
}

export interface CpCustomComponentProps {
  featureName?: string;
  componentId: string;
  postMessageRef?: (postMessage: (message: unknown) => void) => void;
  onIsHelpWidgetOpenChange?: (isHelpWidgetOpen: boolean) => void;
}

// Schemas for a `customComponents` in `settings_json` (RetailerSettings)
const customComponentSchema = z.object({
  name: z.string(),
  url: z.string(),
  versionId: z.number(),
  scopes: z.string().array(),
  settings: z.record(z.union([z.string(), z.number(), z.boolean()])),
});

const settingsResponseSchema = z.object({
  customComponents: z.record(z.unknown()),
});

function pick<T extends {}>(object: T, keys: string[]): Partial<T> {
  const entries = Object.entries(object).filter(([key]) => keys.includes(key));
  return Object.fromEntries(entries) as Partial<T>;
}

const customComponentTrackingEventSchema = z.object({
  eventType: z.union([
    z.literal(NarvarEventTypes.COMPONENT_RENDER),
    z.literal(NarvarEventTypes.CLICK),
  ]),
  clickSubType: z.string().optional(),
});

const CpCustomComponent: FC<CpCustomComponentProps> = ({
  featureName,
  componentId,
  postMessageRef,
  onIsHelpWidgetOpenChange,
}) => {
  const { panelcontainerNoneNone } = useTheme();
  const {
    borderTopLeftRadius = 0,
    borderTopRightRadius = 0,
    borderBottomLeftRadius = 0,
    borderBottomRightRadius = 0,
  } = panelcontainerNoneNone || {}; // can be undefined or null

  const { state } = usePageContext();

  const settingsResponse = useSettings();
  const guestComponentSettings = useMemo(() => {
    // `useSettings` will default to an empty object if the query fails to
    // return RetailerSettings. Also, if an experience does not have any
    // custom components defined, it will be set to `null`. Check for both
    // before attempting to get customComponent settings.
    if (
      Object.keys(settingsResponse).length > 0 &&
      settingsResponse.customComponents !== null
    ) {
      try {
        const validatedSettingsResponse =
          settingsResponseSchema.parse(settingsResponse);

        if (
          validatedSettingsResponse.customComponents[componentId] !== undefined
        ) {
          const validatedCustomComponent = customComponentSchema.parse(
            validatedSettingsResponse.customComponents[componentId],
          );

          return validatedCustomComponent;
        }
      } catch (e) {
        if (e instanceof Error) {
          // eslint-disable-next-line no-console
          console.error(`Invalid custom component settings: ${e.message}`);
        } else {
          // eslint-disable-next-line no-console
          console.error('Invalid custom component settings');
        }
      }
    }
  }, [componentId, settingsResponse]);

  const [guestReadyForMessages, setGuestReady] = useState(false);
  const [guestIsLoaded, setGuestContentLoaded] = useState(false);
  const [iFrameHeight, setIFrameHeight] = useState('0');
  const pingGuestInterval = useRef<number>();

  const logEvent = useAnalyticsPost();
  const baseLogInfo = {
    eventComponent: NarvarEventComponents.NTH_BLOCK_CUSTOM_COMPONENT,
    eventFeature: NarvarEventComponents.NTH_BLOCK_CUSTOM_COMPONENT,
    feature: guestComponentSettings?.name || featureName,
    pageComponent: NarvarEventComponents.NTH_BLOCK_CUSTOM_COMPONENT,
  };

  const [iFrameRef, updateIFrameRef] = useState<HTMLIFrameElement | null>(null);

  useEffect(
    () => {
      if (guestComponentSettings === undefined) return undefined;
      const guestAppUrl = guestComponentSettings.url;

      const receiveMessage = ({
        data: {
          guestReadyToReceiveMessages,
          guestContentLoaded,
          guestDocumentSize,
          isHelpWidgetOpen,
          parentWindowId,
          trackEvent,
        },
        origin,
      }: MessageEvent): void => {
        if (!guestAppUrl.includes(origin) || parentWindowId !== componentId)
          return;

        if (guestReadyToReceiveMessages) setGuestReady(true);
        if (guestContentLoaded) setGuestContentLoaded(true);
        if (guestDocumentSize) setIFrameHeight(guestDocumentSize as string);
        if (isHelpWidgetOpen !== undefined && onIsHelpWidgetOpenChange) {
          onIsHelpWidgetOpenChange(isHelpWidgetOpen as boolean);
        }
        if (trackEvent !== undefined) {
          try {
            const eventValidated =
              customComponentTrackingEventSchema.parse(trackEvent);
            logEvent({
              ...baseLogInfo,
              eventType: eventValidated.eventType,
              clickSubType: eventValidated.clickSubType,
            });
          } catch (e) {
            // eslint-disable-next-line no-console
            console.error(
              e instanceof Error
                ? `Invalid tracking event: ${e.message}`
                : 'Invalid tracking event',
            );
          }
        }
      };

      window.addEventListener('message', receiveMessage);

      return (): void => window.removeEventListener('message', receiveMessage);
    },
    // TODO: Make this dependency array exhaustive
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [guestComponentSettings, componentId],
  );

  const postMessage = useCallback(
    (message: unknown) => {
      if (!iFrameRef) return;
      if (!iFrameRef.contentWindow) return;
      if (!guestComponentSettings) return;
      iFrameRef.contentWindow.postMessage(message, guestComponentSettings.url);
    },
    // TODO: Make this dependency array exhaustive
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [iFrameRef, guestComponentSettings && guestComponentSettings.url],
  );

  useEffect(() => {
    if (postMessageRef) postMessageRef(postMessage);
  }, [postMessageRef, postMessage]);

  useEffect(() => {
    if (!guestComponentSettings) return undefined;

    if (!guestReadyForMessages) {
      const ping = (): void => {
        postMessage({
          type: 'windowId',
          data: {
            parentWindowId: componentId,
          },
        });
      };
      ping();
      pingGuestInterval.current = window.setInterval(ping, PING_INTERVAL_MS);
      return (): void => window.clearInterval(pingGuestInterval.current);
    }
    window.clearInterval(pingGuestInterval.current);

    postMessage({
      type: 'settings',
      data: {
        pageContext: {
          product: state.product,
          experienceHost: state.experienceHost,
          retailerMoniker: state.retailerMoniker,
          versionName: state.versionName,
          ...pick(state, guestComponentSettings.scopes),
        },
        settings: guestComponentSettings.settings,
      },
    });
    return undefined;
  }, [
    componentId,
    guestComponentSettings,
    guestReadyForMessages,
    postMessage,
    state,
  ]);

  useEffect(
    () => {
      if (!guestIsLoaded) return;
      logEvent({
        ...baseLogInfo,
        eventType: NarvarEventTypes.COMPONENT_RENDER,
      });
    },
    // TODO: Make this dependency array exhaustive
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [guestIsLoaded],
  );

  if (guestComponentSettings === undefined) return null;

  return (
    <iframe
      ref={(ref): void => updateIFrameRef(ref)}
      className={styles.iframe}
      // TODO: Set the title of this with a translation
      title="iFrameComponent"
      src={guestComponentSettings.url}
      sandbox="allow-popups allow-scripts allow-same-origin allow-forms allow-presentation allow-top-navigation-by-user-activation"
      style={{
        borderTopLeftRadius,
        borderTopRightRadius,
        borderBottomLeftRadius,
        borderBottomRightRadius,
        display: guestIsLoaded ? 'block' : 'none',
        height: iFrameHeight,
      }}
    />
  );
};

export default CpCustomComponent;
